import type { KeyboardEvent, MouseEvent } from 'react';
import { Heart, Play } from 'lucide-react';
import type { Channel } from '@/lib/channels';
import { Logo } from '@/components/common/Logo';
import { FlagIcon } from '@/components/common/FlagIcon';
import { useFavoritesStore } from '@/store/useFavoritesStore';

interface ChannelCardProps {
  channel: Channel;
  onClick: (channel: Channel) => void;
  isActive?: boolean;
  /** Hide the favorite heart button (e.g. on the Favorites page itself). */
  showFavorite?: boolean;
}

export function ChannelCard({
  channel,
  onClick,
  isActive,
  showFavorite = true,
}: ChannelCardProps) {
  const isFavorite = useFavoritesStore((s) => s.favoriteIds.includes(channel.id));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);

  const handleFavorite = (e: MouseEvent) => {
    // Stop the card's onClick from firing when the user just wants to
    // toggle the heart — otherwise clicking the heart starts playback.
    e.stopPropagation();
    e.preventDefault();
    toggleFavorite(channel.id);
  };

  // Outer element is a div[role=button] (not a <button>) so the heart
  // child can be a real <button> without violating the HTML rule that
  // interactive content can't be nested inside another <button>. We
  // mirror native button keyboard behavior (Enter / Space) ourselves.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(channel);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(channel)}
      onKeyDown={handleKeyDown}
      aria-label={`Play ${channel.name}`}
      className={`group text-left bg-bg-elevated border rounded-lg overflow-hidden hover:border-accent hover:shadow-md transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        isActive ? 'border-accent ring-1 ring-accent' : 'border-border'
      }`}
    >
      <div className="aspect-video bg-white relative flex items-center justify-center">
        <Logo name={channel.name} url={channel.logo} className="w-full h-full" size="lg" />
        {isActive && (
          <div className="absolute top-2 right-2 bg-accent text-white text-xs px-2 py-0.5 rounded">
            LIVE
          </div>
        )}
        {showFavorite && (
          <button
            type="button"
            onClick={handleFavorite}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-pressed={isFavorite}
            className="absolute top-2 left-2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
          >
            <Heart
              className={`w-4 h-4 transition-colors ${
                isFavorite ? 'text-error' : 'text-white'
              }`}
              fill={isFavorite ? 'currentColor' : 'none'}
            />
          </button>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
          <Play
            className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            fill="currentColor"
          />
        </div>
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-sm line-clamp-2 leading-tight mb-1" title={channel.name}>
          {channel.name}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <FlagIcon code={channel.countryCode} size="sm" />
          <span className="truncate">{channel.country}</span>
        </div>
        {/*
          Always reserve the same vertical space for the broadcaster
          badge — even when absent — so cards have a consistent height
          and the virtualizer doesn't re-measure as badges appear or
          disappear during scroll (which was causing a "shaky" feel).
        */}
        <div className="mt-1.5 flex items-center gap-1.5 min-h-[20px]">
          {channel.broadcasterGroup && (
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                channel.broadcasterGroup === 'world-cup'
                  ? 'bg-accent/20 text-accent'
                  : 'bg-success/20 text-success'
              }`}
            >
              {channel.broadcasterGroup === 'world-cup' ? '🏆 WC' : '⭐ Top'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
